/**
 * RelatoriosContagemTab - Reports tab for Contagem page (inline display pattern)
 *
 * Flow: Select cliente (header) → Card grid → Select report → "Gerar Relatório" → inline data → PDF/Excel download
 */
import React, { useState } from 'react';
import {
  FileSpreadsheet, FileText, Package, History,
  ArrowLeft, Download, Loader2, Eye,
  AlertTriangle,
} from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import { downloadRelatorioContagem } from '../../services/relatorioContagemService';
import { useRelatorioContagemData } from '../../hooks/useRelatorioContagemData';
import type { ContagemReportId } from '../../hooks/useRelatorioContagemData';
import {
  ResultadoContagemReport,
  ResumoComparativoReport,
  ColetaContagemReport,
  Ultimas3DetalhadoReport,
} from './relatorios';
import { msg } from '../../utils/messages';
import PermissionGate from '../PermissionGate';

interface RelatoriosContagemTabProps {
  jobId: number;
  globalClienteFilter: (string | number)[];
  jobClientes: Array<{ id: number; nome: string }>;
}

interface ReportCardConfig {
  id: ContagemReportId;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const reportCards: ReportCardConfig[] = [
  {
    id: 'resultado',
    title: 'Resultado da Contagem',
    description: 'Relatório completo com todos os itens, lotes, validade, sistema vs contado.',
    icon: <FileSpreadsheet className="w-6 h-6" />,
  },
  {
    id: 'resumo',
    title: 'Resumo Comparativo',
    description: 'Conferência com dados das últimas contagens, setores e observações.',
    icon: <FileText className="w-6 h-6" />,
  },
  {
    id: 'coleta',
    title: 'Solicitação de Coleta',
    description: 'Itens para coleta com código SAP, lote, quantidade e lacres.',
    icon: <Package className="w-6 h-6" />,
  },
  {
    id: 'ultimas3',
    title: 'Últimas 3 Detalhado',
    description: 'Comparativo detalhado das 3 últimas contagens do hospital.',
    icon: <History className="w-6 h-6" />,
  },
];

export function RelatoriosContagemTab({ jobId, globalClienteFilter, jobClientes }: RelatoriosContagemTabProps) {
  const hook = useRelatorioContagemData(jobId);
  const {
    selectedReport, setSelectedReport,
    reportData, reportMeta,
    loading, error,
    downloading,
    generateReport, downloadReport, clearReport,
  } = hook;

  // Derive clienteId from global header filter (single source of truth)
  const clienteId = globalClienteFilter.length > 0 ? Number(globalClienteFilter[0]) : null;
  const [dataFimUltimas3, setDataFimUltimas3] = useState<string>('');
  const [hasGenerated, setHasGenerated] = useState(false);
  const [downloadingLeituras, setDownloadingLeituras] = useState(false);
  const user = useAuthStore((state) => state.user);

  const handleSelectCard = (reportId: ContagemReportId) => {
    setSelectedReport(reportId);
    setHasGenerated(false);
  };

  const handleBack = () => {
    clearReport();
    setHasGenerated(false);
  };

  const handleGenerate = async () => {
    await generateReport(clienteId || undefined, dataFimUltimas3 || undefined);
    setHasGenerated(true);
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    await downloadReport(format, clienteId || undefined, dataFimUltimas3 || undefined);
  };

  const activeCard = reportCards.find(c => c.id === selectedReport);

  // N92: trilha de leituras do scanner — dado de DIAGNÓSTICO, MASTER-only.
  // Sem card/preview de propósito: não é relatório de operação, é a evidência
  // para investigar "bipei e não achou" (as leituras sem produto só existem aqui).
  const isMaster = user?.role === 'MASTER';
  const handleDownloadLeituras = async () => {
    setDownloadingLeituras(true);
    try {
      await downloadRelatorioContagem(
        `/jobs/${jobId}/leituras/excel`,
        `leituras-contagem-${jobId}.xlsx`,
      );
    } catch (e) {
      console.error('[N92] falha ao baixar leituras', e);
    } finally {
      setDownloadingLeituras(false);
    }
  };

  // Require client selection — show warning if none selected
  if (!clienteId) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Selecione um Cliente
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Selecione um cliente no filtro acima para visualizar os relatórios da contagem.
        </p>
      </div>
    );
  }

  // Grid mode: show card selection
  if (!selectedReport) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Relatórios da Contagem
          </h3>
          {/* N92: MASTER-only, dado de diagnóstico (não é relatório de operação) */}
          {isMaster && (
            <button
              onClick={handleDownloadLeituras}
              disabled={downloadingLeituras}
              title="Todas as leituras de código de barras desta contagem, como saíram do scanner (inclui as que não acharam produto). Uso: diagnóstico."
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <FileSpreadsheet className="w-4 h-4" />
              {downloadingLeituras ? 'Gerando...' : 'Leituras do scanner (debug)'}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reportCards.map((card) => (
            <button
              key={card.id}
              onClick={() => handleSelectCard(card.id)}
              className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200 text-left"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-600 dark:bg-blue-700 rounded-lg text-white flex-shrink-0">
                  {card.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                    {card.title}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {card.description}
                  </p>
                  <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 text-sm font-medium">
                    <Eye className="w-4 h-4" />
                    Visualizar Relatório
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Detail mode: selected report
  return (
    <div className="space-y-4">
      {/* Header: Back + Title + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-600 dark:bg-blue-700 rounded-lg text-white">
              {activeCard?.icon}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {activeCard?.title}
            </h3>
          </div>
        </div>

        {/* Download buttons (only after data loaded) */}
        {hasGenerated && (reportData.length > 0 || reportMeta) && (
          <PermissionGate tela="CONTAGENS_LIST" acao="exportar">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDownload('pdf')}
                disabled={!!downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-md transition-colors"
              >
                {downloading === 'pdf' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                PDF
              </button>
              <button
                onClick={() => handleDownload('excel')}
                disabled={!!downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md transition-colors"
              >
                {downloading === 'excel' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Excel
              </button>
            </div>
          </PermissionGate>
        )}
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap items-end gap-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        {/* Data final filter for ultimas3 */}
        {selectedReport === 'ultimas3' && (
          <div className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Data Final (opcional)</label>
            <input
              type="date"
              value={dataFimUltimas3}
              onChange={(e) => setDataFimUltimas3(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Generate button */}
        <PermissionGate tela="CONTAGENS_LIST" acao="exportar">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Gerar Relatório
          </button>
        </PermissionGate>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Report content */}
      {hasGenerated && (
        <div>
          {selectedReport === 'resultado' && (
            <ResultadoContagemReport data={reportData} loading={loading} meta={reportMeta} />
          )}
          {selectedReport === 'resumo' && (
            <ResumoComparativoReport data={reportData} loading={loading} meta={reportMeta} />
          )}
          {selectedReport === 'coleta' && (
            <ColetaContagemReport data={reportData} loading={loading} meta={reportMeta} />
          )}
          {selectedReport === 'ultimas3' && (
            <Ultimas3DetalhadoReport data={reportData} loading={loading} meta={reportMeta} />
          )}
        </div>
      )}
    </div>
  );
}
